import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconeHistorique } from './icone-historique';

interface MenuActionsCarteProps {
  /** The title of the text, for the name of the button ("actions pour ..."). */
  nom: string;
  /** "Traduire vers un autre noyau": opens the choice of the noyaux to add (#11, N1). */
  onAutreNoyau: () => void;
}

interface Position {
  top: number;
  right: number;
}

/**
 * The button of the actions of a finished card (H8, #11): three vertical dots at the top right, a
 * native button of 32 px (`aria-haspopup="menu"`, `aria-expanded`) that opens a small menu with one
 * item, "Traduire vers un autre noyau". The menu is drawn at the end of `<body>`, placed from the
 * button (`position: fixed`): the zone of the cards scrolls and would cut it. It closes by
 * `Échap` (the focus goes back to the button), by `Tab`, by a click outside, by a scroll or a
 * resize; "Entrée" and "Espace" open it (the native button), and the focus goes to its item.
 * Choosing the item closes the menu and gives the focus back to the button first, so that the
 * modal it opens gives it back there when it closes.
 */
export function MenuActionsCarte({ nom, onAutreNoyau }: MenuActionsCarteProps) {
  const boutonId = useId();
  const menuId = useId();
  const bouton = useRef<HTMLButtonElement>(null);
  const element = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const ouvert = position !== null;

  function ouvrir() {
    const boite = bouton.current?.getBoundingClientRect();
    if (boite === undefined) return;
    setPosition({ top: boite.bottom + 4, right: window.innerWidth - boite.right });
  }

  function fermer(rendreLeFocus: boolean) {
    setPosition(null);
    if (rendreLeFocus) bouton.current?.focus();
  }

  // The focus goes to the item once the menu is drawn.
  useEffect(() => {
    if (ouvert) element.current?.focus();
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const dehors = (evenement: PointerEvent) => {
      const cible = evenement.target;
      if (!(cible instanceof Node)) return;
      if (element.current?.parentElement?.contains(cible) || bouton.current?.contains(cible)) {
        return;
      }
      setPosition(null);
    };
    const replier = () => setPosition(null);
    document.addEventListener('pointerdown', dehors);
    window.addEventListener('resize', replier);
    // Capture: the zone of the cards scrolls, and a scroll does not bubble.
    window.addEventListener('scroll', replier, true);
    return () => {
      document.removeEventListener('pointerdown', dehors);
      window.removeEventListener('resize', replier);
      window.removeEventListener('scroll', replier, true);
    };
  }, [ouvert]);

  function touche(evenement: KeyboardEvent<HTMLElement>) {
    if (evenement.key === 'Escape') {
      evenement.preventDefault();
      evenement.stopPropagation();
      fermer(true);
    } else if (evenement.key === 'Tab') {
      // The focus goes back to the button, and the Tab goes on from there.
      fermer(true);
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(evenement.key)) {
      // One item: the arrows stay on it.
      evenement.preventDefault();
    }
  }

  function choisir() {
    fermer(true);
    onAutreNoyau();
  }

  return (
    <>
      <button
        ref={bouton}
        id={boutonId}
        type="button"
        aria-label={`actions pour ${nom}`}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-controls={ouvert ? menuId : undefined}
        onClick={() => (ouvert ? fermer(false) : ouvrir())}
        onKeyDown={(evenement) => {
          if (evenement.key === 'ArrowDown' && !ouvert) {
            evenement.preventDefault();
            ouvrir();
          } else if (evenement.key === 'Escape' && ouvert) {
            evenement.stopPropagation();
            fermer(true);
          }
        }}
        className="relative z-10 -my-2 -mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100 aria-expanded:bg-gray-100"
      >
        <IconeHistorique name="more_vert" size={20} />
      </button>
      {position === null
        ? null
        : createPortal(
            <div
              id={menuId}
              role="menu"
              aria-labelledby={boutonId}
              onKeyDown={touche}
              style={{ top: position.top, right: position.right }}
              className="fixed z-50 min-w-[240px] rounded-md border border-gray-200 bg-white p-1 shadow-lg"
            >
              <button
                ref={element}
                type="button"
                role="menuitem"
                onClick={choisir}
                className="flex h-10 w-full cursor-pointer items-center rounded-md px-3 text-left font-semibold text-small text-black hover:bg-gray-100 focus-visible:bg-gray-100"
              >
                Traduire vers un autre noyau
              </button>
            </div>,
            document.body,
          )}
    </>
  );
}
